import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { RankTrackLadder } from '@/components/ranks/RankTrackLadder';
import { RankTierStrip } from '@/components/ranks/RankTierStrip';
import { formatRankMetric } from '@/lib/ranks/formatRankMetric';
import { joinRankMetricUnit, rankMetricUnitKey } from '@/lib/ranks/rankMetricUnit';
import { nextUnearnedTierId } from '@/lib/ranks/nextUnearnedTierId';
import { rankProgressPercent } from '@/lib/ranks/rankProgressPercent';
import { RankPatentBadge } from '@/components/ranks/RankPatentBadge';
import { ClipperRankTrackId } from '@/generated/graphql';

type RankMetricTrackId = 'views' | 'campaign_money' | 'affiliate' | 'campaigns' | 'renders';

export type RankTrackCardTrack = {
  id: ClipperRankTrackId;
  metric: string;
  current_tier_id?: string | null;
  unlocked_tier_ids: string[];
  next_threshold?: string | null;
};

function asMetricTrackId(id: ClipperRankTrackId): RankMetricTrackId {
  return id as RankMetricTrackId;
}

export function RankTrackCard({ track }: { track: RankTrackCardTrack }) {
  const { t, i18n } = useTranslation();
  const ladderId = useId();
  const [ladderOpen, setLadderOpen] = useState(false);
  const trackId = asMetricTrackId(track.id);
  const trackName = t(`ranks.tracks.${track.id}`);
  const unitKey = rankMetricUnitKey(trackId);
  const unit = unitKey ? t(unitKey) : null;
  const formattedNext = track.next_threshold
    ? joinRankMetricUnit(
        formatRankMetric({
          trackId,
          metric: track.next_threshold,
          language: i18n.language,
        }),
        unit,
      )
    : null;
  const formattedMetric = joinRankMetricUnit(
    formatRankMetric({
      trackId,
      metric: track.metric,
      language: i18n.language,
    }),
    unit,
  );
  const currentTierId = track.current_tier_id;
  const featuredTierId = currentTierId
    ?? nextUnearnedTierId(trackId, track.unlocked_tier_ids);
  const percent = rankProgressPercent({
    metric: track.metric,
    nextThreshold: track.next_threshold,
  });
  const windowKey = formattedNext
    ? (currentTierId ? 'ranks.rankEndsAt' : 'ranks.rankStartsAt')
    : null;
  const progressLabel = t('ranks.progress', {
    metric: formattedMetric,
    next: formattedNext ?? formattedMetric,
  });

  // Rendered in the card and again in the dialog; only the card's bar carries
  // the test id so queries stay unambiguous while the dialog is open.
  const renderProgress = (testId?: string) => (
    <div className="space-y-1.5">
      {formattedNext ? (
        <p className="text-xs tabular-nums text-muted-foreground">
          {t('ranks.progress', { metric: formattedMetric, next: formattedNext })}
        </p>
      ) : currentTierId ? (
        <p className="text-xs tabular-nums text-muted-foreground">{formattedMetric}</p>
      ) : null}
      <Progress
        value={percent}
        className="h-2"
        data-testid={testId}
        aria-valuenow={percent}
        aria-label={progressLabel}
      />
    </div>
  );

  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader className="p-3 pb-2">
        <CardTitle className="text-sm sm:text-base">{trackName}</CardTitle>
        <CardDescription className="text-xs" data-testid="clipper-ranks-track-metric">
          {t(`ranks.metrics.${track.id}`)}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3 p-3 pt-0">
        {featuredTierId ? (
          <RankPatentBadge
            tierId={featuredTierId}
            size="md"
            earned={Boolean(currentTierId)}
            className="text-sm font-medium"
            data-testid="clipper-ranks-featured-tier"
          />
        ) : (
          <p className="text-sm text-muted-foreground">{t('ranks.emptyTrack')}</p>
        )}
        {windowKey && formattedNext ? (
          <p
            data-testid="clipper-ranks-rank-window"
            className="text-xs leading-snug text-muted-foreground"
          >
            {t(windowKey, { next: formattedNext })}
          </p>
        ) : null}
        {renderProgress('clipper-ranks-track-progress')}
        {/* Pushes the levels row to the card's bottom edge so every card in the
            grid lines its strip and button up, whatever the copy above wraps to. */}
        <div className="mt-auto space-y-1 pt-1">
          <RankTierStrip track={track} />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-between px-1 text-xs text-muted-foreground"
            data-testid="clipper-ranks-ladder-toggle"
            aria-expanded={ladderOpen}
            aria-controls={ladderId}
            onClick={() => setLadderOpen(true)}
          >
            {t('ranks.ladder.show')}
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>

      <Dialog open={ladderOpen} onOpenChange={setLadderOpen}>
        <DialogContent id={ladderId} className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('ranks.ladder.title', { track: trackName })}</DialogTitle>
            <DialogDescription>{t(`ranks.metrics.${track.id}`)}</DialogDescription>
          </DialogHeader>
          {renderProgress()}
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <RankTrackLadder track={track} />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
